import { Country } from '../enums/countries.enum';

/**
 * Interface pour un groupe de pays
 */
export interface CountryGroup {
  label: string;
  countries: string[];
}

/**
 * Helper pour grouper les pays par continent/région
 * Basé sur l'organisation de l'enum Country
 *
 * @see Country enum dans countries.enum.ts
 */
export class CountryGroupsHelper {
  /**
   * Retourne les pays groupés par continent/région
   * L'ordre suit la priorité pour un client européen
   */
  static getGroupedCountries(): CountryGroup[] {
    return [
      {
        label: '🇪🇺 Union Européenne',
        countries: [
          Country.DE, // Allemagne
          Country.AT, // Autriche
          Country.BE, // Belgique
          Country.BG, // Bulgarie
          Country.CY, // Chypre
          Country.HR, // Croatie
          Country.DK, // Danemark
          Country.ES, // Espagne
          Country.EE, // Estonie
          Country.FI, // Finlande
          Country.FR, // France
          Country.GR, // Grèce
          Country.HU, // Hongrie
          Country.IE, // Irlande
          Country.IT, // Italie
          Country.LV, // Lettonie
          Country.LT, // Lituanie
          Country.LU, // Luxembourg
          Country.MT, // Malte
          Country.NL, // Pays-Bas
          Country.PL, // Pologne
          Country.PT, // Portugal
          Country.CZ, // Tchéquie
          Country.RO, // Roumanie
          Country.SK, // Slovaquie
          Country.SI, // Slovénie
          Country.SE, // Suède
        ],
      },
      {
        label: '🌍 Autres pays européens',
        countries: [
          Country.AL, // Albanie
          Country.AD, // Andorre
          Country.AM, // Arménie
          Country.AZ, // Azerbaïdjan
          Country.BY, // Bélarus
          Country.BA, // Bosnie-Herzégovine
          Country.GE, // Géorgie
          Country.IS, // Islande
          Country.XK, // Kosovo
          Country.LI, // Liechtenstein
          Country.MK, // Macédoine du Nord
          Country.MD, // Moldavie
          Country.MC, // Monaco
          Country.ME, // Monténégro
          Country.NO, // Norvège
          Country.GB, // Royaume-Uni
          Country.RU, // Russie
          Country.SM, // Saint-Marin
          Country.RS, // Serbie
          Country.CH, // Suisse
          Country.TR, // Turquie
          Country.UA, // Ukraine
          Country.VA, // Vatican
        ],
      },
      {
        label: '🌍 Afrique',
        countries: [
          Country.ZA, // Afrique du Sud
          Country.DZ, // Algérie
          Country.AO, // Angola
          Country.BJ, // Bénin
          Country.BW, // Botswana
          Country.BF, // Burkina Faso
          Country.BI, // Burundi
          Country.CM, // Cameroun
          Country.CV, // Cap-Vert
          Country.CF, // République centrafricaine
          Country.KM, // Comores
          Country.CG, // Congo-Brazzaville
          Country.CD, // Congo (Rép. dém.)
          Country.CI, // Côte d'Ivoire
          Country.DJ, // Djibouti
          Country.EG, // Égypte
          Country.ER, // Érythrée
          Country.SZ, // Eswatini
          Country.ET, // Éthiopie
          Country.GA, // Gabon
          Country.GM, // Gambie
          Country.GH, // Ghana
          Country.GN, // Guinée
          Country.GW, // Guinée-Bissau
          Country.GQ, // Guinée équatoriale
          Country.KE, // Kenya
          Country.LS, // Lesotho
          Country.LR, // Libéria
          Country.LY, // Libye
          Country.MG, // Madagascar
          Country.MW, // Malawi
          Country.ML, // Mali
          Country.MA, // Maroc
          Country.MU, // Maurice
          Country.MR, // Mauritanie
          Country.MZ, // Mozambique
          Country.NA, // Namibie
          Country.NE, // Niger
          Country.NG, // Nigéria
          Country.UG, // Ouganda
          Country.RW, // Rwanda
          Country.EH, // Sahara occidental
          Country.ST, // Sao Tomé-et-Principe
          Country.SN, // Sénégal
          Country.SC, // Seychelles
          Country.SL, // Sierra Leone
          Country.SO, // Somalie
          Country.SS, // Soudan du Sud
          Country.SD, // Soudan
          Country.TZ, // Tanzanie
          Country.TD, // Tchad
          Country.TG, // Togo
          Country.TN, // Tunisie
          Country.ZM, // Zambie
          Country.ZW, // Zimbabwe
        ],
      },
      {
        label: '🌎 Amérique du Nord',
        countries: [
          Country.CA, // Canada
          Country.US, // États-Unis
          Country.MX, // Mexique
        ],
      },
      {
        label: '🌎 Amérique Centrale et Caraïbes',
        countries: [
          Country.AI, // Anguilla
          Country.AG, // Antigua-et-Barbuda
          Country.AW, // Aruba
          Country.BS, // Bahamas
          Country.BB, // Barbade
          Country.BZ, // Belize
          Country.BM, // Bermudes
          Country.CR, // Costa Rica
          Country.CU, // Cuba
          Country.CW, // Curaçao
          Country.DM, // Dominique
          Country.DO, // République dominicaine
          Country.GD, // Grenade
          Country.GL, // Groenland
          Country.GT, // Guatemala
          Country.HT, // Haïti
          Country.HN, // Honduras
          Country.JM, // Jamaïque
          Country.KY, // Îles Caïmans
          Country.MS, // Montserrat
          Country.NI, // Nicaragua
          Country.PA, // Panama
          Country.PR, // Porto Rico
          Country.BQ, // Pays-Bas caribéens
          Country.KN, // Saint-Christophe-et-Niévès
          Country.LC, // Sainte-Lucie
          Country.SX, // Saint-Martin (partie néerlandaise)
          Country.MF, // Saint-Martin (partie française)
          Country.PM, // Saint-Pierre-et-Miquelon
          Country.VC, // Saint-Vincent-et-les-Grenadines
          Country.SV, // Salvador
          Country.TC, // Îles Turks-et-Caïcos
          Country.VG, // Îles Vierges britanniques
          Country.VI, // Îles Vierges des États-Unis
        ],
      },
      {
        label: '🌎 Amérique du Sud',
        countries: [
          Country.AR, // Argentine
          Country.BO, // Bolivie
          Country.BR, // Brésil
          Country.CL, // Chili
          Country.CO, // Colombie
          Country.EC, // Équateur
          Country.FK, // Îles Malouines
          Country.GF, // Guyane française
          Country.GY, // Guyana
          Country.PY, // Paraguay
          Country.PE, // Pérou
          Country.SR, // Suriname
          Country.UY, // Uruguay
          Country.VE, // Venezuela
        ],
      },
      {
        label: '🌏 Asie',
        countries: [
          Country.AF, // Afghanistan
          Country.SA, // Arabie saoudite
          Country.BH, // Bahreïn
          Country.BD, // Bangladesh
          Country.BT, // Bhoutan
          Country.BN, // Brunei
          Country.KH, // Cambodge
          Country.CN, // Chine
          Country.KP, // Corée du Nord
          Country.KR, // Corée du Sud
          Country.AE, // Émirats arabes unis
          Country.IN, // Inde
          Country.ID, // Indonésie
          Country.IQ, // Irak
          Country.IR, // Iran
          Country.IL, // Israël
          Country.JP, // Japon
          Country.JO, // Jordanie
          Country.KZ, // Kazakhstan
          Country.KG, // Kirghizistan
          Country.KW, // Koweït
          Country.LA, // Laos
          Country.LB, // Liban
          Country.MY, // Malaisie
          Country.MV, // Maldives
          Country.MN, // Mongolie
          Country.MM, // Myanmar
          Country.NP, // Népal
          Country.OM, // Oman
          Country.UZ, // Ouzbékistan
          Country.PK, // Pakistan
          Country.PS, // Palestine
          Country.PH, // Philippines
          Country.QA, // Qatar
          Country.SG, // Singapour
          Country.LK, // Sri Lanka
          Country.SY, // Syrie
          Country.TJ, // Tadjikistan
          Country.TW, // Taïwan
          Country.TH, // Thaïlande
          Country.TL, // Timor oriental
          Country.TM, // Turkménistan
          Country.VN, // Viêt Nam
          Country.YE, // Yémen
        ],
      },
      {
        label: '🌏 Océanie',
        countries: [
          Country.AS, // Samoa américaines
          Country.AU, // Australie
          Country.CK, // Îles Cook
          Country.FJ, // Fidji
          Country.GU, // Guam
          Country.KI, // Kiribati
          Country.MH, // Îles Marshall
          Country.FM, // Micronésie
          Country.NR, // Nauru
          Country.NC, // Nouvelle-Calédonie
          Country.NZ, // Nouvelle-Zélande
          Country.NU, // Niue
          Country.NF, // Île Norfolk
          Country.MP, // Îles Mariannes du Nord
          Country.PW, // Palaos
          Country.PG, // Papouasie-Nouvelle-Guinée
          Country.PN, // Îles Pitcairn
          Country.PF, // Polynésie française
          Country.WS, // Samoa
          Country.SB, // Îles Salomon
          Country.TK, // Tokelau
          Country.TO, // Tonga
          Country.TV, // Tuvalu
          Country.VU, // Vanuatu
          Country.WF, // Wallis-et-Futuna
        ],
      },
      {
        label: "🏝️ Territoires français d'outre-mer",
        countries: [
          Country.BL, // Saint-Barthélemy
          Country.GP, // Guadeloupe
          Country.MQ, // Martinique
          Country.YT, // Mayotte
          Country.RE, // Réunion
          Country.TF, // Terres australes et antarctiques françaises
        ],
      },
      {
        label: '🇨🇳 Régions spéciales administratives chinoises',
        countries: [
          Country.HK, // Hong Kong
          Country.MO, // Macao
        ],
      },
      {
        label: '🏔️ Territoires spéciaux',
        countries: [
          Country.AX, // Åland
          Country.FO, // Îles Féroé
          Country.GG, // Guernesey
          Country.JE, // Jersey
          Country.IM, // Île de Man
          Country.GI, // Gibraltar
          Country.SJ, // Svalbard et Jan Mayen
          Country.AQ, // Antarctique
          Country.BV, // Île Bouvet
          Country.IO, // Territoire britannique de l'océan Indien
          Country.CX, // Île Christmas
          Country.CC, // Îles Cocos
          Country.GS, // Géorgie du Sud-et-les Îles Sandwich du Sud
          Country.HM, // Îles Heard-et-MacDonald
          Country.SH, // Sainte-Hélène, Ascension et Tristan da Cunha
          Country.UM, // Îles mineures éloignées des États-Unis
        ],
      },
    ];
  }

  /**
   * Retourne tous les pays sans groupement (pour compatibilité)
   */
  static getAllCountries(): string[] {
    return Object.values(Country);
  }

  /**
   * Trouve le groupe d'un pays donné
   */
  static getCountryGroup(country: string): string | null {
    const groups = this.getGroupedCountries();
    for (const group of groups) {
      if (group.countries.includes(country)) {
        return group.label;
      }
    }
    return null;
  }
}
